import {
  TableCell,
  TableRow,
  Theme,
  Tooltip,
  makeStyles,
  useTheme,
} from '@material-ui/core';
import { ClassNameMap } from '@material-ui/styles';
import { ReactElement, cloneElement } from 'react';
import { LayoutTemplate, createScaffolderLayout } from '../../../layouts';
import { scaffolderPlugin } from '../../../plugin';
import { UI_STYLE_SAFE_KEY } from '../../utils';
import { applyDefaultsToTemplateStyle } from './utils';

const useStyles = makeStyles(theme => ({
  tableCell: {
    padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
    '& div:first-child': {
      minHeight: 'auto',
    },
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
  },
  firstCell: {
    background: theme.palette.white,
    zIndex: 1,
    position: 'sticky',
    left: 0,
    padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
    '& div:first-child': {
      minHeight: 'auto',
    },
    border: `1px solid ${theme.palette.grey[300]}`,
    borderLeft: 'none',
    borderTop: 'none',
  },
  tableRow: {
    background: theme.palette.white,
    '& .PrivateSwitchBase-root': {
      padding: 0,
    },
  },
}));

function getTableRowCell(
  element: {
    content: ReactElement;
    name: string;
    disabled: boolean;
    readonly: boolean;
    hidden: boolean;
  },
  theme: Theme,
  classes: ClassNameMap<any>,
  index: number,
) {
  const errors: string[] =
    '0' in (element.content.props?.errorSchema ?? {})
      ? element.content.props?.errorSchema?.['0']?.__errors
      : element.content.props?.errorSchema?.__errors;
  const duplicateElement = element.content;

  if (duplicateElement.props.errorSchema?.__errors)
    duplicateElement.props.errorSchema.__errors = errors;

  let uiSchema;

  const templateStyle = duplicateElement.props.uiSchema[UI_STYLE_SAFE_KEY];

  const baseTableStyle = {
    borderRadius: theme.spacing(1),
    width: '100%',
    height: '32px',
    minWidth: 200,
    minHeight: 'auto',
    'ui:popper': 'wide',
  };

  const field = duplicateElement.props.uiSchema['ui:field'];

  switch (field) {
    case 'CustomUrlPicker':
      uiSchema = {
        ...duplicateElement.props.uiSchema,
        [UI_STYLE_SAFE_KEY]: applyDefaultsToTemplateStyle(templateStyle, {
          ...baseTableStyle,
          paddingTop: '4px',
          paddingBottom: '4px',
          overflowY: 'hidden',
          minWidth: '450px',
          maxWidth: '500px',
        }),
      };
      break;
    case 'EntitySearchPicker':
      uiSchema = {
        ...duplicateElement.props.uiSchema,
        [UI_STYLE_SAFE_KEY]: applyDefaultsToTemplateStyle(templateStyle, {
          ...baseTableStyle,
          paddingTop: '4px',
          paddingBottom: '4px',
          overflowY: 'hidden',
          minWidth: '450px',
          maxWidth: '500px',
        }),
      };
      break;
    default:
      uiSchema = {
        ...element.content.props.uiSchema,
        [UI_STYLE_SAFE_KEY]: applyDefaultsToTemplateStyle(
          templateStyle,
          baseTableStyle,
        ),
      };
  }

  const cloned = cloneElement(duplicateElement, {
    schema: {
      ...element.content.props.schema,
      description: '',
      title: '',
    },
    uiSchema: uiSchema,
    // a flag added to the picker props, to allow them to define special behavior when inside this template
    __inTableRowTemplate: true,
  });

  return (
    <TableCell
      key={element.content.key}
      className={index === 0 ? classes.firstCell : classes.tableCell}
    >
      {errors?.length ? (
        <Tooltip title={errors.join('\n')}>
          <div style={{ width: '100%', height: '100%' }}>{cloned}</div>
        </Tooltip>
      ) : (
        cloned
      )}
    </TableCell>
  );
}

export const TableRowTemplate: LayoutTemplate = (props: any) => {
  const theme = useTheme();
  const classes = useStyles();
  const headerProperties: string[] = (props.schema as any)?.columnHeaders
    ? Array.from((props.schema as any)?.columnHeaders?.keys())
    : [];
  const tableRowCells = headerProperties.map((headerProperty, index) => {
    const cell = props.properties.find(
      (p: any) => p.content.props.name === headerProperty,
    );
    if (cell) {
      return getTableRowCell(cell, theme, classes, index);
    }
    return <TableCell key={headerProperty} />;
  });
  return (
    <TableRow className={classes.tableRow}>
      {tableRowCells}
      {(props.schema as any).RowButtons}
    </TableRow>
  );
};

export const TableRowTemplateLayout = scaffolderPlugin.provide(
  createScaffolderLayout({
    name: 'TableRowTemplate',
    component: TableRowTemplate,
  }),
);
